import { KnownId } from "@fairfleet/geotab";
import { LiveDataRow } from "../models/LiveDataRow";

const MAX_DECIMALS = 3;

/**
 * Metres per kilometre.
 *
 * Only used for the odometer where the user's measurement profile defines no conversion for it.
 * Geotab reports odometers in metres.
 */
const METRES_PER_KM = 1000;

const SYMBOL_BY_UNIT = new Map<string, string>();

/**
 * Shortens Geotab's unit names to what belongs next to a number.
 *
 * A unit arrives either as one of Geotab's enum ids or as the display name from the user's
 * measurement profile, depending on whether that profile covers the diagnostic — so both spellings
 * have to end up as the same symbol. The enum ids come from `KnownId` rather than being typed out,
 * because they are easy to misremember: it is `UnitOfMeasureVoltsId`, not `UnitOfMeasureVoltId`.
 */
function alias(symbol: string, ...names: string[]): void {
  for (const name of names) {
    SYMBOL_BY_UNIT.set(name.toLowerCase(), symbol);
  }
}

alias("%", KnownId.UnitOfMeasurePercentageId, "%", "percent", "percentage");
alias("A", KnownId.UnitOfMeasureAmpsId, "a", "amp", "amps", "ampere");
alias("V", KnownId.UnitOfMeasureVoltsId, "v", "volt", "volts");
alias("W", KnownId.UnitOfMeasureWattsId, "w", "watt", "watts");
alias("rpm", KnownId.UnitOfMeasureRevolutionsPerMinuteId, "rpm", "u/min", "revolutions per minute");
alias("l", KnownId.UnitOfMeasureLitersId, "l", "liter", "liters", "litre", "litres");
alias("gal", "gal", "gallon", "gallons");
alias("m", KnownId.UnitOfMeasureMetersId, "m", "meter", "meters", "metre", "metres");
alias("km", "km", "kilometer", "kilometers", "kilometre", "kilometres");

export function normalizeUnitOfMeasure(unitOfMeasure: string): string {
  if (unitOfMeasure === KnownId.UnitOfMeasureNoneId) {
    return "";
  }

  const trimmed = unitOfMeasure.trim();
  return SYMBOL_BY_UNIT.get(trimmed.toLowerCase()) ?? unitOfMeasure;
}

/**
 * A reading as one checklist line: the number in the language in use, with its unit.
 *
 * Converted into the unit the user's measurement profile asks for. Geotab reports every reading
 * in a base unit and leaves the conversion to the profile, so showing the raw number next to the
 * profile's unit name would label, say, a value in metres as kilometres.
 *
 * Not grouped, because a checklist line is read as a single value rather than scanned down a
 * column of figures, and thousands separators only add noise there.
 *
 * @param row - The reading, or `undefined` when the value was never reported.
 * @param language - The language the app is running in.
 */
export function formatChecklistValue(row: LiveDataRow | undefined, language: string): string {
  if (!row) {
    return "";
  }

  const value = formatNumber(inDisplayUnit(row), language, { useGrouping: false });
  if (value === "") {
    // Nothing readable to attach a unit to; a bare " V" would look like a broken row.
    return "";
  }

  const unit = normalizeUnitOfMeasure(row.unitOfMeasure);
  return unit ? `${value} ${unit}` : value;
}

/**
 * An odometer reading, in the unit the user asked for.
 *
 * Grouped, unlike the other values: an odometer is a large figure and unreadable without it.
 *
 * Where the measurement profile covers the diagnostic, its conversion decides — a user on
 * imperial units gets miles. Only where it does not is the reading assumed to be the metres
 * Geotab's own odometer diagnostics report, and shown as kilometres.
 */
export function formatOdometerValue(row: LiveDataRow | undefined, language: string): string {
  if (!row || typeof row.value !== "number" || !Number.isFinite(row.value)) {
    return "";
  }

  const unit = normalizeUnitOfMeasure(row.unitOfMeasure);
  if (row.unitFactor !== undefined && unit !== "") {
    return `${formatNumber(inDisplayUnit(row), language, { useGrouping: true })} ${unit}`;
  }

  const kilometres = unit === "km" ? row.value : row.value / METRES_PER_KM;
  return `${formatNumber(kilometres, language, { useGrouping: true })} km`;
}

/**
 * A serial number exactly as reported.
 *
 * Never run through a number formatter, and never converted: that would group the digits and drop
 * leading zeros, and a serial is an identifier rather than a quantity.
 */
export function formatSerialValue(row: LiveDataRow | undefined): string {
  return row ? String(row.value) : "";
}

/** The reading in the unit the user's measurement profile asks for. */
function inDisplayUnit(row: LiveDataRow): number | string {
  if (typeof row.value !== "number" || !Number.isFinite(row.value)) {
    return row.value;
  }

  return row.value * (row.unitFactor ?? 1) + (row.unitOffset ?? 0);
}

function formatNumber(
  value: number | string,
  language: string,
  options: { useGrouping: boolean },
): string {
  if (typeof value === "string") {
    return value;
  }
  if (!Number.isFinite(value)) {
    return "";
  }

  return value.toLocaleString(language, {
    maximumFractionDigits: MAX_DECIMALS,
    useGrouping: options.useGrouping,
  });
}
